import { jsPDF } from "jspdf";

// Builds a simple, readable PDF of a completed onboarding form submission
// (admin download from the onboarding detail page). The submission is stored
// as flat JSONB key/value pairs, so the PDF is a label/value listing with the
// drawn signature embedded as an image when present.

interface FormSubmissionPdfInput {
  formName: string;
  employeeName: string;
  submittedAt: string | null;
  data: Record<string, any>;
  /** Form field keys in the order they are asked in the UI. Entries whose key
   * is not listed here are appended at the end in their stored order. */
  fieldOrder?: string[];
}

// Bookkeeping keys saved alongside the answers — not part of the form itself.
const INTERNAL_KEYS = new Set([
  "lastSavedStep",
  "lastSavedAt",
  "workRightsEvidenceKey",
]);

// Field order per onboarding form, matching the order questions are asked in
// the frontend forms (client/src/pages/onboarding/...). The submission is
// stored as JSONB, which does not preserve insertion order, so the PDF lists
// fields in this order instead. Keys are the form ids from
// backend/constants/onboardingForms.constants.js.
export const ONBOARDING_FORM_FIELD_ORDER: Record<string, string[]> = {
  "new-starter-form": [
    // Step 1: Personal Information
    "legalFirstName",
    "legalLastName",
    "dateOfBirth",
    "gender",
    "mobilePhone",
    "personalEmail",
    "residentialAddress",
    "suburb",
    "state",
    "postcode",
    // Step 2: Emergency Contacts
    "emergencyContact1Name",
    "emergencyContact1Relationship",
    "emergencyContact1Phone",
    "emergencyContact2Name",
    "emergencyContact2Relationship",
    "emergencyContact2Phone",
    // Step 3: Banking Details
    "accountName",
    "bankName",
    "bsb",
    "accountNumber",
    // Step 4: Work Rights
    "workRightsDocumentType",
    "workRightsEvidenceName",
    "workRightsEvidenceUrl",
    // Step 5: Electronic Signature
    "fullName",
    "signatureDate",
    "signatureType",
    "signatureData",
    "agreed",
  ],
  "tfn-declaration": [
    // Step 1: Your Details
    "taxFileNumber",
    "noTfnYet",
    "title",
    "legalFirstName",
    "legalLastName",
    "homeAddress",
    "suburb",
    "state",
    "postcode",
    "dateOfBirth",
    // Step 2: Employment & Tax Options
    "basisOfPayment",
    "isAustralianResident",
    "claimTaxFreeThreshold",
    "hasHelpDebt",
    "hasSslDebt",
    "hasFinancialSupplement",
    "hasNoStudyLoan",
    // Step 3: Declaration & Signature
    "fullName",
    "signatureDate",
    "signatureType",
    "signatureData",
    "agreed",
  ],
  "super-choice": [
    // Step 1: Your Details
    "legalFirstName",
    "legalLastName",
    "dateOfBirth",
    "mobilePhone",
    // Step 2: Fund Choice
    "choiceType",
    "isSMSF",
    "fundName",
    "fundAbn",
    "usi",
    "memberNumber",
    "smsfFundName",
    "smsfAbn",
    "smsfBankBsb",
    "smsfBankAccount",
    "esa",
    // Step 3: Declaration & Signature
    "fullName",
    "signatureDate",
    "signatureType",
    "signatureData",
    "agreed",
  ],
  "induction-checklist": [
    // Step 1: Induction Checklist
    "checklist",
    // Step 2: Sign-off & Signature
    "fullName",
    "signatureDate",
    "signatureType",
    "signatureData",
    "agreed",
  ],
  "health-declaration": [
    // Step 1: Your Details
    "legalFirstName",
    "legalLastName",
    "dateOfBirth",
    "positionTitle",
    "mobilePhone",
    // Step 2: Health Questions
    "qInjury",
    "qInjuryDetails",
    "qManualHandling",
    "qManualHandlingDetails",
    "qMedication",
    "qMedicationDetails",
    "qAllergies",
    "qAllergiesDetails",
    "qSensory",
    "qSensoryDetails",
    "qWorkersComp",
    "qWorkersCompDetails",
    "qTreatment",
    "qTreatmentDetails",
    "qFitness",
    "qFitnessDetails",
    // Step 3: Declaration & Signature
    "fullName",
    "signatureDate",
    "signatureType",
    "signatureData",
    "agreed",
  ],
};

// Orders data entries by the form's UI field order. Keys missing from
// fieldOrder (unexpected/legacy keys) keep their stored relative order and
// are appended after the known fields.
export const orderFormEntries = (
  data: Record<string, any>,
  fieldOrder?: string[]
): [string, any][] => {
  const entries = Object.entries(data || {});
  if (!fieldOrder?.length) return entries;
  const rank = new Map(fieldOrder.map((key, index) => [key, index]));
  return entries.sort(([a], [b]) => {
    const rankA = rank.has(a) ? (rank.get(a) as number) : fieldOrder.length;
    const rankB = rank.has(b) ? (rank.get(b) as number) : fieldOrder.length;
    return rankA - rankB;
  });
};

// "emergencyContact1Name" -> "Emergency Contact 1 Name"
const humanizeKey = (key: string): string =>
  key
    .replace(/([a-z])([0-9])/g, "$1 $2")
    .replace(/([0-9])([A-Za-z])/g, "$1 $2")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^\w/, (c) => c.toUpperCase());

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const isImageDataUrl = (value: any): value is string =>
  typeof value === "string" && value.startsWith("data:image");

const sanitizeFilename = (name: string): string =>
  name.replace(/[^a-z0-9\- ]/gi, "").trim();

export const downloadFormSubmissionPdf = ({
  formName,
  employeeName,
  submittedAt,
  data,
  fieldOrder,
}: FormSubmissionPdfInput): void => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(formName, margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Employee: ${employeeName}`, margin, y);
  y += 5;
  const submittedLabel = submittedAt
    ? new Date(submittedAt).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";
  doc.text(`Submitted: ${submittedLabel}`, margin, y);
  y += 4;

  doc.setDrawColor(220);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;
  doc.setTextColor(0);

  // Entries
  orderFormEntries(data || {}, fieldOrder).forEach(([key, value]) => {
    if (INTERNAL_KEYS.has(key)) return;

    const label = humanizeKey(key);

    if (isImageDataUrl(value)) {
      // Drawn signature (canvas data URL) — embed as an image.
      try {
        const props = doc.getImageProperties(value);
        const width = 60;
        const height = (props.height / props.width) * width;
        ensureSpace(6 + height + 8);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(label, margin, y);
        y += 4;
        doc.addImage(value, props.fileType, margin, y, width, height);
        y += height + 8;
      } catch {
        // Unreadable image data — fall back to a text placeholder.
        ensureSpace(12);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(label, margin, y);
        y += 5;
        doc.setFont("helvetica", "normal");
        doc.text("(signed)", margin, y);
        y += 8;
      }
      return;
    }

    const lines = doc.splitTextToSize(formatValue(value), contentWidth);
    ensureSpace(6 + lines.length * 5 + 3);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(label, margin, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.text(lines, margin, y);
    y += lines.length * 5 + 3;
  });

  const filename = sanitizeFilename(`${employeeName} - ${formName}`) || "form-submission";
  doc.save(`${filename}.pdf`);
};
